import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useRouter } from "next/router";
import { initializeSearchParams } from "@/search/Langston/SearchPageProvider/SearchPageProvider.utils";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useSearchParamsControls } from "@/search/Langston/useSearchParamsControls";

import {
  type ISearchResults,
  useInfiniteSearchQuery,
} from "@/search/Langston/useInfiniteSearchQuery";

import { useTaxonomyConceptsQuery } from "@/search/Langston/useTaxonomyConceptsQuery";
import { worksheets } from "@/search/Langston/worksheets";
import { readingSkills } from "@/search/Langston/readingSkills";
import {
  CONTENT_SETTINGS_TOGGLE_KEY,
  SEARCH_RESULTS_PER_PAGE,
  SELECTED_WORKSHEET,
  VIEW_ALL_STANDARDS,
} from "../SearchPage/SearchPage.constants";

export const SearchPageContext = createContext<any>(undefined);

const SearchPageProvider = ({ children }: ISearchPageProvider) => {
  const { query, push } = useRouter();
  const { needle: currentNeedle, prompt: currentPrompt } = query;

  const [searchParams, setSearchParams] = useState(
    initializeSearchParams(query),
  );
  const [debouncedParams, setDebouncedParams] = useState<string>(
    searchParams.toString(),
  );

  const [hasUpdatedTaxonomyFilter, setHasUpdatedTaxonomyFilter] =
    useState<boolean>(false);

  const [sendAdminContentSettings, setSendAdminContentSettings] =
    useState<boolean>(false);

  const [viewAllStandards, setViewAllStandards] = useState<boolean>(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<boolean>(false);

  const [isDisabledFilters, setIsDisabledFilters] = useState<boolean>(false);

  // Will refetch data any time debouncedParams changes.
  const {
    data: searchResponseData,
    fetchNextPage,
    hasNextPage,
    refetch: refetchSearchResults,
    isFetching: isSearchLoading, // both initial data and pagination loads
  } = useInfiniteSearchQuery({
    urlParams: debouncedParams,
  });

  // Computed value that maps the useInfiniteQuery response into a more
  // readable structure, consolidating only the values we need.
  const searchResponse = useMemo(() => {
    if (!searchResponseData || !searchResponseData.pages.length)
      return undefined;

    const firstPageResults = (searchResponseData.pages[0] as ISearchResults)
      ?.results;
    const totalResultCount =
      firstPageResults.length < SEARCH_RESULTS_PER_PAGE ||
      searchResponseData.pages[0]?.total_result_count === 0
        ? firstPageResults.length
        : searchResponseData.pages[0]?.total_result_count;

    return {
      results: searchResponseData?.pages.flatMap((page) => page.results),
      total_result_count: totalResultCount,
    };
  }, [searchResponseData]);

  const searchResults = useMemo(() => {
    if (isDisabledFilters) {
      // When isDisabledFilters is true, it means that the worksheets filter
      // is selected. Since we are hardcoding worksheets and setting them as
      // searchResults when selected, we have to return early here to avoid
      // updating searchResults with the actual API response.
      return worksheets;
    } else {
      return searchResponse?.results || [];
    }
  }, [searchResponse, isDisabledFilters]);

  const { data: taxonomyConcepts, isLoading: isLoadingTaxonomyData } =
    useTaxonomyConceptsQuery();

  const {
    appendSearchParam,
    deleteSearchParamValues,
    deleteSingleParamValue,
    getAllSearchParams,
    getSingleSearchParamValue,
    getSearchParamValues,
    hasSearchParamValue,
    setMaturityLevelParam,
    setSearchParamValue,
    toggleSearchParamValue,
  } = useSearchParamsControls({
    searchParams,
    setSearchParams,
  });

  const handleWorksheets = (isEnabled: boolean) => {
    if (isEnabled) {
      setIsDisabledFilters(true);
    } else {
      setIsDisabledFilters(false);
    }
  };

  // Updates to the searchParams via the custom functions from useSearchParamsControls()
  // will trigger this useEffect, and the search params to be pushed to the URL.
  useEffect(() => {
    void push({
      pathname: "/search",
      search: `?${searchParams.toString()}`,
    });
  }, [searchParams.toString()]);

  // Debounces all changes to searchParams
  useEffect(() => {
    const searchParamsKey = searchParams.toString();
    const handler = setTimeout(() => setDebouncedParams(searchParamsKey), 1000);

    return () => clearTimeout(handler);
  }, [searchParams.toString()]);

  // Handle needle and prompt param changes since it's fired from outside
  // of the search context provider in the global nav.
  useEffect(() => {
    const previousNeedle = searchParams.get(LangstonParameterSchema.needle.key);
    const previousPrompt = searchParams.get(LangstonParameterSchema.prompt.key);

    // Handle clearing needle
    if (!currentNeedle) {
      deleteSingleParamValue(LangstonParameterSchema.needle.key);
    }

    // Handle clearing prompt
    if (!currentPrompt) {
      deleteSingleParamValue(LangstonParameterSchema.prompt.key);
    }

    // Handle setting a new needle
    if (!!currentNeedle && currentNeedle !== previousNeedle) {
      setSearchParamValue(
        LangstonParameterSchema.needle.key,
        currentNeedle as string,
      );
    }

    // Handle setting a new prompt
    if (!!currentPrompt && currentPrompt !== previousPrompt) {
      setSearchParamValue(
        LangstonParameterSchema.prompt.key,
        currentPrompt as string,
      );
    }
  }, [currentNeedle, currentPrompt]);

  // Handle query params that aren't part of our Langston parameter schema,
  // since they'll get sanitized out of the url after initial load.
  // These external params provide a way to inform the page context of a
  // given state or value.
  // i.e. Linking to the search page with ?admin_content_settings=true will
  // toggle on the content settings view.
  useEffect(() => {
    if (query[CONTENT_SETTINGS_TOGGLE_KEY] === "true") {
      setSendAdminContentSettings(true);
    }

    if (query[VIEW_ALL_STANDARDS] === "true") {
      setViewAllStandards(true);
    }

    if (query[SELECTED_WORKSHEET] === "true") {
      setSelectedWorksheet(true);
    }
  }, [query]);

  return (
    <SearchPageContext.Provider
      value={{
        getters: {
          readingSkills,
          searchResponse,
          searchResults,
          isDisabledFilters,
          hasNextPage,
          hasUpdatedTaxonomyFilter,
          isSearchLoading,
          sendAdminContentSettings,
          taxonomyConcepts,
          isLoadingTaxonomyData,
          searchParams,
          viewAllStandards,
          selectedWorksheet,
        },
        handlers: {
          appendSearchParam,
          deleteSearchParamValues,
          deleteSingleParamValue,
          fetchNextPage,
          getAllSearchParams,
          getSingleSearchParamValue,
          getSearchParamValues,
          handleWorksheets,
          hasSearchParamValue,
          refetchSearchResults,
          setSearchParamValue,
          setMaturityLevelParam,
          setSendAdminContentSettings,
          setHasUpdatedTaxonomyFilter,
          toggleSearchParamValue,
        },
      }}
    >
      {children}
    </SearchPageContext.Provider>
  );
};

export const useSearchPageContext = () => {
  const searchPageContext = useContext(SearchPageContext);

  if (searchPageContext === undefined) {
    throw new Error(
      "searchPageContext must be used within a SearchPageProvider.",
    );
  }

  return searchPageContext;
};

interface ISearchPageProvider {
  children: ReactElement;
}
SearchPageProvider.displayName = "SearchPageProvider";
export { SearchPageProvider };
